import {Box, Flex, HStack, Stack, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React from 'react'
import {useIntl} from 'react-intl'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {Box2OutlineIcon} from '../../../components/icons'
import {ContentAssetModal, useContentAssetModal} from '../../../hooks/use-content-asset-modal'
import AddToRegistryCTA from '../../../components/add-to-registry-cta'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import WhereToBuyCTA from '../../../components/where-to-buy-cta'
import useMultiSite from '../../../hooks/use-multi-site'
import ProductAvailability from './product-availability'
import ProductDisplayPrice from '../../../components/product-display-price'
import PayLater from '../../../components/pay-later'
import useProductPricing from '../../../hooks/use-product-pricing'
import StyledContent from '../../../components/styled-content'
import QuantityPicker from '../../../components/quantity-picker'
import {useCroVariantActive} from '../../../components/cro'
import {CRO_EXPERIMENTS} from '../../../constants'

const CockpitOrderSection = ({
    derivedProduct,
    isGetPropsLoading,
    isProductASet,
    isPromotionsLoading,
    isVariantLoading,
    product,
    onAddToCartClick,
    onRecommendedProductClick
}) => {
    const {site} = useMultiSite()
    const {shippingContentAssetId} = useOcapiSite()
    const contentModal = useContentAssetModal(shippingContentAssetId)
    const {formatMessage} = useIntl()
    const {currency, displayPrice} = useProductPricing(product) || {}
    const {
        isOrderable,
        minOrderQuantity,
        maxOrderQuantity,
        quantity,
        stepQuantity,
        onChangeQuantity
    } = derivedProduct

    const cro68Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro68BnplPdpReposition.experimentId,
        CRO_EXPERIMENTS.cro68BnplPdpReposition.variantId
    )

    return (
        <Stack spacing={4}>
            {!site?.isContentOnlySite && !isProductASet && (
                <Stack spacing={0}>
                    <Flex gap={4.5} alignItems="flex-end" justifyContent="space-between">
                        <ProductAvailability
                            loading={isVariantLoading}
                            flexShrink="0"
                            product={product}
                        />
                        <ProductDisplayPrice
                            product={product}
                            loading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            variant="pdp"
                        />
                    </Flex>
                </Stack>
            )}
            {!isProductASet && (
                <>
                    {!cro68Active && (
                        <PayLater
                            amount={displayPrice}
                            currency={currency}
                            paypalPlacement="product"
                            klarnaPlacement="credit-promotion-badge"
                        />
                    )}
                    <Stack spacing={3}>
                        {isOrderable && !site?.isContentOnlySite && (
                            <QuantityPicker
                                containerProps={{
                                    // matches the button style
                                    maxWidth: {md: '25rem'}
                                }}
                                btnWidth={15}
                                height={14}
                                minQuantity={minOrderQuantity}
                                maxQuantity={maxOrderQuantity}
                                quantity={quantity}
                                quantityStep={stepQuantity}
                                onChange={onChangeQuantity}
                            />
                        )}
                        <AddToCartCTA
                            isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            product={product}
                            quantity={quantity}
                            onClick={onAddToCartClick}
                            onRecommendedProductClick={onRecommendedProductClick}
                        />
                        {!site?.isContentOnlySite && <WhereToBuyCTA product={product} />}
                    </Stack>
                </>
            )}
            <Stack spacing={4}>
                {product?.c_estimatedDelivery && (
                    <HStack spacing={2} alignItems="center">
                        <Box2OutlineIcon boxSize={4} />
                        <Text textStyle="Regular Small">
                            {formatMessage(
                                {
                                    id: 'checkout.shipping.delivery_time',
                                    defaultMessage: 'Delivery: {time}',
                                    description:
                                        'The date range when delivery is expected to happen. {time} contains range like "24.11.2021 - 26.11.2021"'
                                },
                                {time: product.c_estimatedDelivery}
                            )}
                        </Text>
                    </HStack>
                )}

                {cro68Active && (
                    <Box paddingBlock={4}>
                        <PayLater
                            amount={displayPrice}
                            currency={currency}
                            paypalPlacement="product"
                            klarnaPlacement="credit-promotion-badge"
                        />
                    </Box>
                )}

                <Stack spacing={4} paddingTop={2} alignItems="flex-start">
                    <AddToRegistryCTA product={product} />
                    {product.c_pdpCockpitUspContent && (
                        <StyledContent content={product.c_pdpCockpitUspContent} />
                    )}
                </Stack>
            </Stack>
            <ContentAssetModal {...contentModal} variant="pdp" />
        </Stack>
    )
}

CockpitOrderSection.propTypes = {
    derivedProduct: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    isProductASet: PropTypes.bool,
    product: PropTypes.object,
    onAddToCartClick: PropTypes.func,
    onRecommendedProductClick: PropTypes.func
}

export default CockpitOrderSection
